import React, { useState } from 'react';
import { Building2, Layers, Settings, Send, X, CheckCircle2, ArrowRight } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const lavagePlans = [
  {
    id: 'entretien-ext',
    title: 'Plan entretien extérieur',
    frequency: '2 fois par année',
    icon: Building2,
    color: 'from-cyan-500 to-blue-600',
    features: [
      'Toutes les fenêtres extérieures',
      'Cadres et rebords',
      'Service au printemps et à l\'automne',
      'Entretien régulier pour garder un bâtiment toujours propre',
    ],
  },
  {
    id: 'complet-int-ext',
    title: 'Plan complet intérieur + extérieur',
    frequency: '2 fois par année',
    icon: Layers,
    color: 'from-blue-500 to-indigo-600',
    popular: true,
    features: [
      'Toutes les fenêtres intérieures et extérieures',
      'Cadres et rebords',
      'Nettoyage complet au printemps et à l\'automne',
      'Idéal pour un résultat impeccable toute l\'année',
    ],
  },
  {
    id: 'personnalise',
    title: 'Plan personnalisé',
    frequency: 'Service sur mesure',
    icon: Settings,
    color: 'from-indigo-500 to-purple-600',
    features: [
      'Plan adapté à votre bâtiment',
      'Fréquence et services personnalisés',
      'Options: lavage intérieur, extérieur ou scellant',
      'Contactez-nous pour créer votre plan',
    ],
  },
];

const PlanCards = ({ onNavigateToScellant }) => {
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          plan: `${selectedPlan.title} – ${selectedPlan.frequency}`,
          service: 'Lavage résidentiel',
        }),
      });

      if (response.ok) {
        toast({
          title: 'Demande envoyée!',
          description: 'Nous vous contacterons rapidement pour votre soumission.',
        });
        setFormData({ name: '', address: '', phone: '', email: '' });
        setSelectedPlan(null);
      } else {
        throw new Error('Erreur');
      }
    } catch {
      toast({
        title: 'Erreur',
        description: 'Veuillez réessayer ou nous appeler au 819.200.2015',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="plans" className="pt-16 md:pt-20 pb-12 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-800">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-6 md:mb-14">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-2 md:mb-3">
            Nos{' '}
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-pink-500 bg-clip-text text-transparent">
              forfaits
            </span>
          </h2>
          <p className="text-sm md:text-lg text-gray-400 max-w-xl mx-auto">
            Choisissez votre plan et demandez votre soumission gratuite.
          </p>
        </div>

        {/* Plan Cards - 2x2 mobile, 4 cols desktop */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 max-w-6xl mx-auto">
          {/* Lavage plans */}
          {lavagePlans.map((plan) => {
            const Icon = plan.icon;
            return (
              <button
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                onClick={() => setSelectedPlan(plan)}
                className={`relative group text-left rounded-xl md:rounded-2xl border transition-all duration-300 p-3 md:p-7 hover:-translate-y-1 hover:shadow-2xl ${
                  plan.popular
                    ? 'border-cyan-500/50 bg-gray-800/80 shadow-lg shadow-cyan-500/10'
                    : 'border-white/10 bg-gray-800/50 hover:border-cyan-500/30'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-[9px] md:text-xs font-bold px-2 md:px-4 py-0.5 md:py-1 rounded-full whitespace-nowrap">
                    Populaire
                  </div>
                )}
                <div className={`inline-flex bg-gradient-to-br ${plan.color} rounded-lg md:rounded-xl p-2 md:p-3 mb-2 md:mb-4`}>
                  <Icon className="text-white" size={18} />
                </div>
                <h3 className="text-xs md:text-xl font-bold text-white mb-0.5 md:mb-1 leading-tight">{plan.title}</h3>
                <p className="text-cyan-400 font-semibold text-[10px] md:text-sm mb-2 md:mb-4">{plan.frequency}</p>
                <ul className="hidden md:block space-y-2 mb-5">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                      <CheckCircle2 size={14} className="text-cyan-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="text-center bg-white/5 group-hover:bg-gradient-to-r group-hover:from-cyan-500 group-hover:to-blue-600 text-white py-1.5 md:py-2.5 rounded-lg md:rounded-xl font-semibold text-[9px] md:text-sm transition-all">
                  Soumission
                </div>
              </button>
            );
          })}

          {/* Scellant d'asphalte card */}
          <button
            data-testid="plan-card-scellant"
            onClick={() => {
              if (onNavigateToScellant) onNavigateToScellant();
            }}
            className="relative group text-left rounded-xl md:rounded-2xl border border-white/10 bg-gray-800/50 hover:border-red-500/40 transition-all duration-300 p-3 md:p-7 hover:-translate-y-1 hover:shadow-2xl hover:shadow-red-500/5"
          >
            <div className="inline-flex bg-gradient-to-br from-red-500 to-red-700 rounded-lg md:rounded-xl p-2 md:p-3 mb-2 md:mb-4">
              <svg className="text-white w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
            </div>
            <h3 className="text-xs md:text-xl font-bold text-white mb-0.5 md:mb-1 leading-tight">Scellant d'asphalte</h3>
            <p className="text-red-400 font-semibold text-[10px] md:text-sm mb-2 md:mb-4">Service optionnel</p>
            <ul className="hidden md:block space-y-2 mb-5">
              <li className="flex items-center gap-2 text-sm text-gray-300">
                <CheckCircle2 size={14} className="text-red-500 flex-shrink-0" />
                Protection de votre asphalte
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-300">
                <CheckCircle2 size={14} className="text-red-500 flex-shrink-0" />
                Application professionnelle
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-300">
                <CheckCircle2 size={14} className="text-red-500 flex-shrink-0" />
                Soumission gratuite
              </li>
            </ul>
            <div className="flex items-center justify-center gap-1 bg-white/5 group-hover:bg-gradient-to-r group-hover:from-red-500 group-hover:to-red-700 text-white py-1.5 md:py-2.5 rounded-lg md:rounded-xl font-semibold text-[9px] md:text-sm transition-all">
              <span>Découvrir</span>
              <ArrowRight size={12} className="hidden md:inline" />
            </div>
          </button>
        </div>
      </div>

      {/* Modal Form */}
      {selectedPlan && (
        <div
          data-testid="plan-modal-overlay"
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
          onClick={() => setSelectedPlan(null)}
        >
          <div
            data-testid="plan-modal-form"
            className="bg-gray-900 border border-white/10 rounded-2xl p-6 md:p-8 w-full max-w-md relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="plan-modal-close"
              onClick={() => setSelectedPlan(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>

            <div className="mb-6">
              <h3 className="text-xl font-bold text-white mb-1">{selectedPlan.title}</h3>
              <p className="text-cyan-400 font-semibold text-sm">{selectedPlan.frequency}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-1.5">Nom *</label>
                <Input
                  data-testid="plan-form-name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Votre nom complet"
                  className="h-11 bg-gray-800 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-1.5">Adresse <span className="text-gray-500 font-normal">(optionnel)</span></label>
                <Input
                  data-testid="plan-form-address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Rue Principale, Ville, QC"
                  className="h-11 bg-gray-800 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-1.5">Téléphone *</label>
                <Input
                  data-testid="plan-form-phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(819) 555-0100"
                  className="h-11 bg-gray-800 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-1.5">Courriel *</label>
                <Input
                  data-testid="plan-form-email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre@courriel.com"
                  className="h-11 bg-gray-800 border-white/10 text-white"
                />
              </div>
              <Button
                data-testid="plan-form-submit"
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold shadow-lg transition-all"
              >
                {isSubmitting ? 'Envoi en cours...' : (
                  <>
                    <Send className="mr-2" size={18} />
                    Envoyer ma demande
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default PlanCards;
